import { WorkspaceManager as WSM } from "./workspaceManager";
import { StateType } from "./windowStorageManager";
import { ActiveWorkspace, WorkspaceImport, GroupData, Workspace } from "./types";
import { Trace } from "./workspaceManager";
import { FinsembleWindow } from "../../common/FinsembleWindow";
import { wrapWithTimeout, isStackedWindow, isEqualIgnoringArrayOrder } from "../../common/util";
import { RouterClient } from "../../clients/routerClient";
import { toast } from "../../common/toast";
import { FinsembleWindowData } from "../window/types";
import { BaseService } from "../baseService";
import { WORKSPACE, DOCKING } from "../../common/constants";
import { LauncherClient } from "../../clients/launcherClient";
import { StorageClient } from "../../clients/storageClient";
import { ConfigClient } from "../../clients/configClient";
import { SearchClient } from "../../clients/searchClient";
import { Logger } from "../../clients/logger";
import SystemManagerClient from "../../common/systemManagerClient";
import PrivateEventManager from "../../common/events/PrivateEventManager";
import { ConfigUtilInstance as configUtil } from "../../common/configUtil";
import { deprecatedGetActiveAndPendingWindowNames } from "../../deprecated/deprecatedFunctions";
import { bringWindowsToFront, minimizeWindows } from "../../common/Desktop";
import { DialogManagerClient } from "../../clients/dialogManagerClient";
import get from "lodash/get";
import set from "lodash/set";
import negate from "lodash/negate";
import clone from "lodash/cloneDeep";
import pLimit from "p-limit";
import PQueue from "p-queue";
import merge from "deepmerge";

const limit = pLimit(1);

Logger.start();

const LIFECYCLE = "APPLICATION LIFECYCLE: ";

const isNotStackedWindow = negate(isStackedWindow);

const {
	GET_ACTIVE_WORKSPACE_DIRTY,
	GET_WORKSPACES,
	EXPORT,
	SAVE_AS,
	SAVE,
	SWITCH_TO,
	IMPORT,
	REMOVE,
	RENAME,
	SET_WORKSPACE_ORDER,
	GET_ACTIVE_WORKSPACE,
	GET_WORKSPACE_NAMES,
	NEW_WORKSPACE,
	SET_WINDOW_STATE,
	GET_WINDOW_STATE,
	ADD_WINDOW,
	REMOVE_WINDOW,
} = WORKSPACE.API_CHANNELS;

const { ACTIVE_WORKSPACE, CACHE_STORAGE_TOPIC, UPDATE_PUBSUB, INITIAL_WORKSPACE_PREFERENCE, PUBLISH_REASONS } =
	WORKSPACE;

const DROP_ON_CLOSE = true;

export type WorkspaceStateUpdate = {
	activeWorkspace: ActiveWorkspace;
	workspaces: String[];
	groups: Record<string, GroupData>;
	workspacesConfig: Array<any>;
	reason: string;
};

const channelToOldReason = {
	NEW_WORKSPACE: PUBLISH_REASONS.NEW_WORKSPACE,
	ADD_WINDOW: PUBLISH_REASONS.WINDOW_ADDED,
	REMOVE_WINDOW: PUBLISH_REASONS.WINDOW_REMOVED,
	REMOVE: PUBLISH_REASONS.WORKSPACE_REMOVED,
	RENAME: PUBLISH_REASONS.WORKSPACE_RENAMED,
	SAVE_AS: PUBLISH_REASONS.SAVE_AS,
};

/**
 * Setting *what* happened in the workspace service is very
 * easy; just look at the many local logs. Seeing *why* something
 * happened is quite difficult, as everything is based on chained
 * async calls. This tracing mechanism helps with that by adding
 * the reason for each request as well as an incremented counter.
 */
let traceCounter = -1;
/**
 * This should never be directly accessed. Instead, use the value
 * returned by `setGlobalTrace`.
 */
let _globalTrace: Trace;

/**
 * Sets the global trace. Note, if this is called out of band,
 *
 * NOTE - Should only be called from within `WorkspaceService.init()`
 * or `WorkspaceService.createRouterEndpoints()`.
 */
function setGlobalTrace(reason: string): Trace {
	traceCounter++;
	_globalTrace = {
		reason,
		counter: traceCounter,
	};
	return _globalTrace;
}

/**
 * Confirms wether a variable passed to it exists and is a number.
 * If true, returns the parsed Number, otherwise returns false
 * @param {string} [num] A string potentially containing a number
 * @returns False or Number(input)
 */
const isNumber = (num: string) => {
	if (!num || Number.isNaN(Number(num))) {
		return false;
	}

	return Number(num);
};

/** If, for whatever reason, there are no configured workspaces
 * use this instead. Also used when workspace switch times out.
 */
const emptyWS: WorkspaceImport = {
	name: "Empty Workspace",
	componentStates: {},
	groups: {},
	type: "workspace",
	version: "1.0.0",
	windowData: [],
	windows: [],
};

const WORKSPACE_CONFIGS = {
	PROMPT_USER_ON_DIRTY_WORKSPACE: "servicesConfig.workspace.promptUserOnDirtyWorkspace",
	DISCARD_UNSAVED_CHANGES: "servicesConfig.workspace.discardUnsavedChanges",
	DEPRECATED_PROMPT_ON_DIRTY: "preferences.workspaceService.promptUserOnDirtyWorkspace", // DEPRECATED - retrieving it for backwards compatibility
};

/**
 * WorkspaceService is an event handler that acts as the
 * public interface into workspaces for the rest of the system
 * (mostly by way of the WorkspaceClient).
 *
 * Most of its functionality is merely to pass messages received from
 * the router service to the WorkspaceManager, which does the heavy
 * lifting. This keeps the WorkspaceManager lean, agnostic of the rest
 * of the system, and easier to test.
 *
 * The rest of the functionality of the WorkspaceService is to handle
 * the loading and closing of the windows in a workspace. This requires
 * a bit of a song and dance (there's an order of operations that must
 * be obeyed). However, the WorkspaceService eagerly awaits the coming
 * of the Multi-Window Client, which will allow operations on sets of
 * windows, and obviate much of the complex load/close behavior here.
 *
 * Once there is a MultiWindow Client handling load/close, and once
 * workspace and window states are stored in a reactive store like the
 * persistent store, all the Workspace Service's function will have been
 * outsourced; at that point, it could probably be absorbed by the the
 * Window service, which would eliminate certain timing issues (see the
 * odd state-machine formed by WorkspaceService.loading/closingWorkspace)
 */
class WorkspaceService extends BaseService {
	//Used only to determine if a updated workspace state should be published
	private static cachedWorkspaceState: WorkspaceStateUpdate;

	private static lifecycle: "loading" | "closing" | "switching" | "stable" | "shuttingDown";

	private static closeWorkspaceTimeout: number;

	private static workspaceServiceReady = false;

	private static closeWorkspaceTimeoutMessage: string;

	private static eventManager: PrivateEventManager = new PrivateEventManager({
		name: "workspaceClient",
		sourceType: "Workspace",
	});

	private static concurrentSpawnLimit: number;

	private static queuedSwitchRequest: string | null = null;

	private static limitedSpawningQueue: PQueue;

	private static currentlySpawningWindowNames: Record<string, any>;

	private static cleaningOutstandingSpawns = false;

	private static startupApp: { [key: string]: any };

	private static isResetRequested: boolean;

	private static isRestartRequested: boolean;

	private static scheduledCloseDiscardUnsavedChanges: any;

	private static configWorkspacesExist: boolean;

	constructor(params) {
		super(params);
	}

	setReady() {
		WorkspaceService.workspaceServiceReady = true;
	}

	initialize = async () => {
		this.doShutdown = this.doShutdown.bind(this);
		this.onShutdown(this.doShutdown);

		const trace = setGlobalTrace(PUBLISH_REASONS.INIT);

		/**
		 * Previous versions used a slightly different storage scheme.
		 * Therefore we need to import any workspaces from the legacy location
		 * on startup.
		 */
		await WSM.importLegacyWorkspaces(trace).catch((err) => Logger.system.error("importLegacyWorkspaces failed.", err));

		const { data } = await ConfigClient.getValues([{ field: "startup_app" }, { field: "finsemble" }]);
		const { data: userPreferences } = await ConfigClient.getPreferences();

		if (!data) {
			Logger.system.error(
				"WorkspaceService::initialize() Couldn't fetch `start_app` and `finsemble` fields from config"
			);
			return;
		}

		const { startup_app, finsemble } = data;

		WorkspaceService.startupApp = startup_app;

		// Grab the workspaceService defaults from config
		WorkspaceService.closeWorkspaceTimeout =
			get(finsemble, "servicesConfig.workspace.closeWorkspaceTimeout") ||
			get(finsemble, "services.workspaceService.closeWorkspaceTimeout") ||
			30000;

		WorkspaceService.closeWorkspaceTimeoutMessage = get(
			finsemble,
			"servicesConfig.workspace.closeFailureNotificationMessage",
			"Problem closing current workspace. Please close any windows remaining on your screen and then select another workspace to open."
		);

		emptyWS.name = get(finsemble, "servicesConfig.workspace.emptyWorkspaceName", emptyWS.name);

		const processorConfigValue =
			get(finsemble, "servicesConfig.launcher.concurrentSpawnLimit") ||
			get(finsemble, "services.launcherService.concurrentSpawnLimit");

		const configValueNum = isNumber(processorConfigValue);
		if (!configValueNum) {
			WorkspaceService.concurrentSpawnLimit = Math.ceil(navigator.hardwareConcurrency / 2);
		} else {
			WorkspaceService.concurrentSpawnLimit = Math.ceil(configValueNum);
		}
		Logger.system.debug(`Workspace Init: concurrentSpawnLimit is ${WorkspaceService.concurrentSpawnLimit}`);

		// Grab deprecated path in config if any
		const deprecatedPromptOnDirty = get(finsemble, WORKSPACE_CONFIGS.DEPRECATED_PROMPT_ON_DIRTY, null);
		// Grab the current prompt on dirty config if we have one (if we don't we will get the default value from schema)
		const promptOnDirtyWorkspaceFromConfig = get(finsemble, WORKSPACE_CONFIGS.PROMPT_USER_ON_DIRTY_WORKSPACE);
		// Grab a preference if the user has set one - we need to retrieve this separately to correctly handle the deprecated config vs. user preference on new config
		const promptOnDirtyWorkspaceFromPreferences = get(
			userPreferences,
			`finsemble.${WORKSPACE_CONFIGS.PROMPT_USER_ON_DIRTY_WORKSPACE}`,
			null
		);

		// load value from config
		let promptOnDirty = promptOnDirtyWorkspaceFromConfig;
		// allow deprecated config to override
		if (deprecatedPromptOnDirty !== null) {
			Logger.system.deprecated(
				`Workspace Init: preferences.workspaceService.promptUserOnDirtyWorkspace is present in config but it has been marked as deprecated. Please, use finsemble.servicesConfig.workspace.promptUserOnDirtyWorkspace`
			);
			promptOnDirty = deprecatedPromptOnDirty;

			// allow user preference to override
			if (promptOnDirtyWorkspaceFromPreferences !== null) {
				Logger.system.log(
					`Workspace Init: ignoring preferences.workspaceService.promptUserOnDirtyWorkspace present in config since user preference has been set.`
				);
				promptOnDirty = promptOnDirtyWorkspaceFromPreferences;
			}
		}

		WSM.promptUserOnDirtyWorkspace = promptOnDirty;
		WSM.autosave = !WSM.promptUserOnDirtyWorkspace && !get(finsemble, WORKSPACE_CONFIGS.DISCARD_UNSAVED_CHANGES, false);

		WSM.discardUnsavedChanges = get(finsemble, WORKSPACE_CONFIGS.DISCARD_UNSAVED_CHANGES, false);

		const workspaceNames = await WSM.getWorkspaceNames(trace, false);

		//Cache workspace names
		await StorageClient.save({
			topic: CACHE_STORAGE_TOPIC,
			key: WORKSPACE.ALL_WORKSPACES,
			value: workspaceNames,
		});

		Logger.system.debug(`Workspace Init: workspaceNames are ${workspaceNames}`);

		WorkspaceService.configWorkspacesExist = Boolean(finsemble.workspaces && finsemble.workspaces.length);

		// Create an empty workspace if none exists in config or storage, import any workspaces provided in config
		const defaultIfNoConfig = !workspaceNames.length ? [emptyWS] : [];
		const workspaces: WorkspaceImport[] = WorkspaceService.configWorkspacesExist
			? finsemble.workspaces
			: defaultIfNoConfig;

		WSM.setWorkspaceConfig(workspaces);

		// We don't want to import any workspaces already in storage, so we filter them out.
		// AVOID changing this into concurrent calls! The workspaces need to be added sequentially.
		for (const ws of workspaces.filter((x) => !workspaceNames.includes(x.name))) {
			await WSM.importWorkspace(trace, ws).catch((err) => Logger.system.error("importWorkspace failed.", err, ws));
		}

		// DH 3/6/2019 - If registration fails, this will hang the entire WS launch
		await WorkspaceService.registerSearch();

		await WorkspaceService.createRouterEndpoints();

		if (await WSM.doesActiveWorkspaceExist(trace)) {
			await WorkspaceService.publishUpdate(trace, PUBLISH_REASONS.LOAD_DATA_RETRIEVED).catch((err) => {
				Logger.system.warn("Inital publish failed", err);
			});
		}

		const initPref = await new Promise((resolve) => ConfigClient.getPreferences((err, res) => resolve(res)));
		Logger.system.debug("Workspace Init: initPref", initPref);

		let checkForDirtyShutdown = !(await configUtil.getDefault(
			finsemble,
			"finsemble.servicesConfig.workspace.disableDirtyShutdownCheck",
			false
		));
		const dirtyShutdown =
			checkForDirtyShutdown &&
			((await WSM.wasLastShutdownDirty(trace).catch((err) =>
				Logger.system.error("wasLastShutdownDirty failed.", err)
			)) ||
				false);
		Logger.system.debug("`Workspace Init: dirtyShutdown", dirtyShutdown);

		await WorkspaceService.setupInitialWorkspace(trace, initPref, dirtyShutdown, workspaces);
		Logger.system.log(LIFECYCLE, "Finished initializing");

		WorkspaceService.lifecycle = "loading";

		// the service will come online without waiting for all the initial workspace to load (which could take a long time);
		// also this waits until toolbar will come up in earlyUser stage before loading initial workspace in user stage.
		await WorkspaceService.waitUntilRightStageToOpenInitialWorkspace(trace);

		// now that initial workspace is loaded will drive to stable state -- user can only switch workspaces from stable state;
		// this also implies the initial workspace load can't be preempted (for now).
		WorkspaceService.lifecycle = "stable";

		/**
		 * After initial workspace is loaded, we set the CLEAN_SHUTDOWN flag to false, so that it acts like
		 * a sort of dead-man switch; if Finsemble shuts down without setting it to true (i.e, because
		 * of a crash, etc.), the active workspace will be loaded in its dirty state.
		 */
		await WSM.setCleanShutdownFlag(trace, false).catch((err) =>
			Logger.system.error("setCleanShutdownFlag failed.", err)
		);

		// Configuration listeners to update the WSM
		ConfigClient.addListener(
			{ field: `finsemble.${WORKSPACE_CONFIGS.PROMPT_USER_ON_DIRTY_WORKSPACE}` },
			(err, config) => {
				Logger.system.log("WorkspaceService new promptUserOnDirtyWorkspace config", config);
				WSM.promptUserOnDirtyWorkspace = !!config.value;
			}
		);
		ConfigClient.addListener({ field: `finsemble.${WORKSPACE_CONFIGS.DISCARD_UNSAVED_CHANGES}` }, (err, config) => {
			Logger.system.log("WorkspaceService new discardUnsavedChanges config", config);
			WSM.autosave = !WSM.promptUserOnDirtyWorkspace && !(config ?? false);
		});
	};

	async cleanShutdown() {
		const trace = setGlobalTrace("shuttingDown");
		WorkspaceService.lifecycle = "shuttingDown";
		Logger.system.log(LIFECYCLE, "Shutdown detected.");
		await WSM.setCleanShutdownFlag(trace, true);
		Logger.system.log("Clean shutdown recorded in storage.");
	}

	doShutdown() {
		const promiseResolver = async (resolve) => {
			const trace = setGlobalTrace("doShutdown");

			const aws: any = await WSM.getActiveWorkspace(trace)
				.catch()
				.catch(() => {
					WorkspaceService.isResetRequested = true;
					Logger.system.debug("No active workspace so must be shutting down after a reset operation");
				});

			if (!WSM.promptUserOnDirtyWorkspace) {
				if (!WSM.discardUnsavedChanges) {
					await WorkspaceService.saveWorkspace(trace);
				}
				await this.cleanShutdown();
				resolve();
			} else {
				if (WorkspaceService.scheduledCloseDiscardUnsavedChanges) {
					await this.cleanShutdown();
					resolve();
				} else {
					// bypass shutdown handling if resetting storage in dev environment
					if (!WorkspaceService.isResetRequested && !WSM.autosave && (await WSM.isActiveWorkspaceDirty(trace))) {
						const { data: shutdownTimeout } = await ConfigClient.getValue({
							field: "finsemble.shutdownTimeout",
						});

						// the shutdown timeout is already partly used up, so shorten to insure this dialog times out before shutdown is forced;
						let promptTimeout = shutdownTimeout - 5000;

						DialogManagerClient.open(
							"yesNo",
							{
								monitor: "primary",
								title: "Save your workspace?",
								question: `Would you like to save \"${aws.name}\" before ${
									!WorkspaceService.isRestartRequested ? "shutting down" : "restarting"
								}?`,
								showTimer: true,
								timerDuration: promptTimeout,
								negativeResponseLabel: "Don't Save",
								affirmativeResponseLabel: "Save",
								showCancelButton: false,
							},
							async (err, response) => {
								if (err || response.choice === "expired") {
									Logger.system.info("doShutdown timer expired so not invoking cleanShutdown");
								} else if (response.choice === "affirmative") {
									await WorkspaceService.saveWorkspace(trace);
									await this.cleanShutdown();
								} else {
									await this.cleanShutdown();
								}
								resolve();
							}
						);
					} else {
						await this.cleanShutdown();
						resolve();
					}
				}
			}
		};
		return new Promise(promiseResolver);
	}

	/**
	 * Wait until startup's user stage, then load the initial workspace.
	 */
	static async waitUntilRightStageToOpenInitialWorkspace(trace: Trace): Promise<void> {
		SystemManagerClient.waitForBootStage("earlyuser", "stageCompleted", async () => {
			try {
				let aws = await WSM.getActiveWorkspace(trace);
				Logger.system.log(LIFECYCLE, `Loading initial workspace ${aws.name}`);

				await WorkspaceService.load(trace, aws.name, aws).catch((err) =>
					Logger.system.error("Initial workspace load caught underlying error: ", err)
				);
			} catch (err) {
				Logger.system.error("initial workspace load failed", err);
				toast(
					"dev",
					"ALWAYS",
					"FSBL-Internal-Workspace-Error",
					"Initial workspace load failed. Could not read workspace data from storage. Internet connectivity may be down."
				);
			}
		});
	}

	/**
	 * Set up the workspace to be initially loaded using the active workspace store in cached storage.
	 */
	static async isMasterStorageReachable(trace: Trace): Promise<boolean> {
		let reachable = true;
		await WSM.getLastUsedWorkspaceName(trace).catch((err) => {
			Logger.system.error("isMasterStorageReachable failed.", err);
			reachable = false;
		});
		return reachable;
	}

	/**
	 * Set up the workspace to be initially loaded using the active workspace store in cached storage.
	 */
	static async setupInitialWorkspaceFromCacheStorage(trace: Trace, initialWorkspaceName): Promise<boolean> {
		let initialWorkspaceLoadSuccessful = true;
		Logger.system.log(LIFECYCLE, `Loading last active workspace "${initialWorkspaceName}"`);
		await WSM.setActiveWorkspace(trace, initialWorkspaceName, true).catch(
			() => (initialWorkspaceLoadSuccessful = false)
		);
		if (!initialWorkspaceLoadSuccessful) {
			toast(
				"dev",
				"ALWAYS",
				"FSBL-Internal-Workspace-Error",
				"Failed loading initial workspace due to systemic failure in storage."
			);
		}
		return initialWorkspaceLoadSuccessful;
	}

	/**
	 * Tries to set the active workspace given a workspace name. This method is invoked
	 * on the several attemps that Finsemble makes to set an active workspace (initial workspace, last loaded, default, etc...)
	 *
	 * @param trace
	 * @param workspaceName The workspace name to be set as an active workspace
	 * @param stage stage of setting the active workspace
	 * @returns A promise boolean true for success, false otherwise
	 */
	private static async _maybeSetActiveWorkspace(
		trace: Trace,
		workspaceName: string,
		stage: "initial" | "last used" | "empty" | "default"
	): Promise<boolean> {
		return WSM.setActiveWorkspace(trace, workspaceName)
			.then(() => true)
			.catch((error) => {
				if (stage) {
					if (stage === "empty") {
						Logger.system.error("Failed loading empty workspace", error);
					} else {
						Logger.system.warn(
							LIFECYCLE,
							`Failed to load ${stage} workspace so will load alternate. Could not read workspace data from storage. Internet connectivity may be down.`
						);
					}
				}
				Logger.system.error(LIFECYCLE, error);
				return false;
			});
	}

	/**
	 * Tries to set an initial workspace.
	 *
	 * Checks whether the initialWorkspace name exists in the list of workspace names,
	 * and if it does tries to set that workspace as the active one.
	 * Otherwise checks whether we have a initial workspace preference set. If we do,
	 * then we unset the preference.
	 *
	 * We always return false if an active workspace is not set.
	 *
	 * @param trace
	 * @param initialWorkspaceName The initial workspace name to set
	 * @returns true if initial workspace is set successfully, false otherwise
	 */
	private static async _maybeSetInitialWorkspaceOrClearPreference(
		trace: Trace,
		initialWorkspaceName: string
	): Promise<boolean> {
		Logger.system.log(LIFECYCLE, `Loading user-specified default workspace "${initialWorkspaceName}"`);
		try {
			const workspaceNames: string[] = await WSM.getWorkspaceNames(trace, false);

			// if we have initialWorkspaceName in the list of available workspaces - try to set it
			if (workspaceNames.includes(initialWorkspaceName)) {
				return this._maybeSetActiveWorkspace(trace, initialWorkspaceName, "initial");
			} else {
				const { data: initialWorkspacePreference } = await ConfigClient.getValue(INITIAL_WORKSPACE_PREFERENCE);
				// initialWorkspaceName does not exist in the list of available workspaces
				// if the preference is set we need to unset the preference
				if (initialWorkspacePreference) {
					Logger.system.warn(
						LIFECYCLE,
						`Initial workspace "${initialWorkspaceName}" not present in available workspaces list. Unsetting initial workspace preference."`
					);

					return ConfigClient.unsetPreference({
						field: INITIAL_WORKSPACE_PREFERENCE,
					})
						.then(() => false)
						.catch((error) => {
							Logger.system.error(
								LIFECYCLE,
								"Failed to unset initial workspace user preference (after the workspace wasn't available).",
								error
							);
							return false;
						});
				}
				return false;
			}
		} catch (error) {
			Logger.system.error(LIFECYCLE, error);
			Logger.system.warn(
				LIFECYCLE,
				"Failed to load default workspace so will load alternate. Could not read workspace data from storage. Internet connectivity may be down."
			);
			return false;
		}
	}

	/**
	 * Set up the workspace to be initially loaded. Multiple paths considered depending on state, config, and error conditions.
	 */
	static async setupInitialWorkspace(
		trace: Trace,
		initPref,
		dirtyShutdown: Boolean,
		workspaces: WorkspaceImport[]
	): Promise<string> {
		Logger.system.log(
			LIFECYCLE,
			"Loading initial workspace",
			initPref,
			dirtyShutdown ? "dirty shutdown" : "not dirty shutdown",
			workspaces
		);

		if (dirtyShutdown) {
			const errorMsg = `${
				WorkspaceService.startupApp.name || "Finsemble"
			} was not shutdown properly. Attempting to load cached version of last active workspace.`;
			Logger.system.warn(LIFECYCLE, errorMsg);
			toast("dev", "ALWAYS", "FSBL-Internal-Workspace-Error", errorMsg);
			await WorkspaceService.setupInitialWorkspaceFromCacheStorage(trace, ACTIVE_WORKSPACE);
			return ACTIVE_WORKSPACE;
		}

		let initialWorkspaceLoadSuccessful = false;
		let initialWorkspaceName: string = initPref[INITIAL_WORKSPACE_PREFERENCE];

		// if user preferences specify the workspace to load we check to see if the workspace exits
		// if it doesn't we unset the preference
		if (initialWorkspaceName) {
			initialWorkspaceLoadSuccessful = await this._maybeSetInitialWorkspaceOrClearPreference(
				trace,
				initialWorkspaceName
			);
		}

		// otherwise this is the normal path -- load the last used workspace
		if (!initialWorkspaceLoadSuccessful) {
			try {
				initialWorkspaceName = await WSM.getLastUsedWorkspaceName(trace);
				// if have a last used workspace name, use it. Otherwise
				// we are likely on a very first run of Finsemble and therefore we move on to the next if statement
				if (initialWorkspaceName) {
					Logger.system.log(LIFECYCLE, `Loading last used workspace "${initialWorkspaceName}"`);
					initialWorkspaceLoadSuccessful = await this._maybeSetActiveWorkspace(
						trace,
						initialWorkspaceName,
						"last used"
					);
				}
			} catch (error) {
				Logger.system.error("wasLastShutdownDirty failed.", error);
				initialWorkspaceName = "";
			}
		}

		// if there was an error above or neither case above applies (i.e. very first run of Finsemble)
		if (!initialWorkspaceLoadSuccessful) {
			// if there's a workspace in cache storage, then use it ()
			const awsExists = await WSM.doesActiveWorkspaceExist(trace);
			if (awsExists) {
				const errorMsg = `${
					WorkspaceService.startupApp.name || "Finsemble"
				} could not load initial workspace from master storage so attempting to load from local cache`;
				Logger.system.warn(LIFECYCLE, errorMsg);
				initialWorkspaceLoadSuccessful = await WorkspaceService.setupInitialWorkspaceFromCacheStorage(
					trace,
					ACTIVE_WORKSPACE
				);
				initialWorkspaceName = ACTIVE_WORKSPACE;
			} else {
				// The first time Finsemble starts there will be no active workspace. Check to see if
				// one of the workspaces has been set with the "default" flag, otherwise the first workspace
				// in the list will become the initial workspace.
				const defaultWorkspace = workspaces.find((workspaceEntry) => workspaceEntry.default);
				initialWorkspaceName = defaultWorkspace ? defaultWorkspace.name : workspaces[0].name;
				initialWorkspaceLoadSuccessful = await this._maybeSetActiveWorkspace(trace, initialWorkspaceName, "initial");
				Logger.system.log(LIFECYCLE, `Loading default workspace in config "${initialWorkspaceName}"`);
			}
		}

		// if still don't have a workspace then use empty
		if (!initialWorkspaceLoadSuccessful) {
			Logger.system.log(LIFECYCLE, "Loading empty workspace");
			await WSM.addWorkspace(trace, emptyWS).catch((err) => Logger.system.error("Failed adding empty workspace", err));
			initialWorkspaceLoadSuccessful = await this._maybeSetActiveWorkspace(trace, emptyWS.name, "empty");
		}

		// if never could get an initial workspace then put out a final error
		if (!initialWorkspaceLoadSuccessful) {
			Logger.system.error(LIFECYCLE, "Failed loading initial workspace due to systemic failure in storage.");
		}

		// Notify that initial workspace load finished
		await WorkspaceService.publishUpdate(trace, PUBLISH_REASONS.LOAD_FINISHED);

		return initialWorkspaceName;
	}

	/**
	 * Registers the service as a Search Provider.
	 * See https://documentation.finsemble.com/tutorial-Search.html
	 */
	static async registerSearch() {
		// Because this isn't in the global queue, we can't set the global trace,
		// as doing so could throw off the count.
		const trace: Trace = { reason: "Out of Band-Search Provider" };

		function handleSearch(params, origin, callback) {
			if (!params.action || !params.item) return;
			if (params.action.name === "Switch") {
				WorkspaceService.handleSwitchTo(trace, params.item.name)
					.then(() => callback(null, "success"))
					.catch((err) => callback(err));
			}
		}

		// DH 3/6/2019 - If registration fails, this will hang the entire WS launch
		const registerSearchPromiseResolver = (resolve) => {
			SearchClient.register(
				{
					name: "Installed Workspaces",
					searchCallback: ({ text }, cb) => WSM.searchWorkspaces(trace, text).then((results) => cb(null, results)),
					itemActionCallback: handleSearch,
					providerActionCallback: () => {},
				},
				resolve
			);
		};
		return new Promise(registerSearchPromiseResolver);
	}

	private static addResponder(channel: string, dropOnClose: boolean, handler: Function) {
		RouterClient.addResponder(channel, async (err, message) => {
			const trace = setGlobalTrace(channel);
			if (err || !message) {
				Logger.system.error(`Workspace Router.addResponder failure for ${channel}`, err);
				return;
			}
			const sendError = (error) => {
				const errToSend = error instanceof Error ? error.toString() : error;
				message.sendQueryResponse(errToSend, null);
			};
			try {
				if (!WorkspaceService.workspaceServiceReady) {
					Logger.system.error("workspaceService invoked before ready", message);
				}
				Logger.system.info(
					`TRACE-${traceCounter}-Handling response for ${channel}. Lifecycle status: ${WorkspaceService.lifecycle}`
				);
				Logger.system.debug(`TRACE-${traceCounter}-Message data`, message);

				if (dropOnClose && WorkspaceService.lifecycle === "closing") {
					const msg = "Workspace is currently closing. Dropping request.";
					Logger.system.log(msg);
					sendError(msg);
					return;
				}

				// typically requests are serialized/queued, but don't do this for the switchTo or they will all queue up and add additional stress to the system (we only want to queue last one);
				// admittedly not serializing switch-to like everything else opens theoretical edge conditions if our APIs were used differently, but the alternative introduces more risk now
				if (channel === SWITCH_TO) {
					const response = await handler(trace, message.data);
					message.sendQueryResponse(null, response);
				} else {
					// here is where incoming request are serialized though the limit function
					const handlerPromise = () => handler(trace, message.data);
					const limitedHandlerPromise = await limit(handlerPromise);
					message.sendQueryResponse(null, limitedHandlerPromise);
				}

				await WorkspaceService.publishUpdate(trace, channel);
			} catch (error) {
				Logger.system.error(error);
				sendError(error);
			}
		});
	}

	/**
	 * Creates the public API for the window service
	 * as router endpoints.
	 *
	 * Divided into 3 sections: query responders, listeners,
	 * and pub/sub responders. Ideally, the listeners and pub/sub
	 * responders would be gotten rid of by just making calls
	 * to the WorkspaceClient at the appropriate time.
	 *
	 * DH 3/11/2019 - We should confirm our clients don't listen
	 * to those transmission channels/pub-sub topics and just remove
	 * them if they don't.
	 */
	static async createRouterEndpoints() {
		// ///////// Query Responders /////////////
		/**
		 * An array of 3-tuples:
		 *
		 * First element is the channel to respond two.
		 *
		 * Second element is a function whose first argument
		 * is the current trace, and whose second argument is
		 * data received along with the query message. If the
		 * function returns a promise, the promise will be awaited.
		 *
		 * Third element is an optional boolean that indicates
		 * whether the request should be dropped when the active
		 * workspace is switching (i.e loading or closing). Default
		 * is not to drop (`false`).
		 */
		const responders = [
			// ////// Special behavior implemented in this class////////
			// /////////////////////////////////////////////////////////
			// We shouldn't really have such methods. The way a
			// developer ought to build batch window actions would be:
			//
			// windowSet = await WorkspaceClient.getWindowSet();
			// await WindowClient.minimize(windowSet);
			["WorkspaceService.bringWindowsToFront", (trace) => WorkspaceService.handleBringWindowsToFront(trace)],
			["WorkspaceService.minimizeAll", (trace) => WorkspaceService.handleMinimizeAll(trace)],

			// //////////////// Pass-through to WSM ////////////////////////
			// //////////////////////////////////////////////////////////////
			[GET_ACTIVE_WORKSPACE, WSM.getActiveWorkspace],
			[NEW_WORKSPACE, (trace, { workspaceName }) => WSM.newWorkspace(trace, workspaceName)],
			// DH 3/16/2019 A bug in the UI code lets you hit export before you've selected a WS
			// so we need a guard.
			[EXPORT, (trace, { workspaceName }) => workspaceName && WSM.exportWorkspace(trace, workspaceName, toast)],
			/**
			 * If force is false, importing may result in a new name.
			 * To inform the caller of the new names, IMPORT returns
			 * an object which maps the original names to the new names.
			 * (if force is false, these will be identical)
			 */
			[
				IMPORT,
				async (trace, { workspaceJSONDefinition, force }) => {
					let results: Record<string, string> = {};
					let inputNames = Object.keys(workspaceJSONDefinition as Record<string, string | WorkspaceImport>);
					/**
					 * AVOID changing this into concurrent calls! The workspaces need to be added sequentially.
					 */
					for (let i = 0; i < inputNames.length; i++) {
						results[inputNames[i]] = await WSM.importWorkspace(trace, workspaceJSONDefinition[inputNames[i]], force);
					}
					return results;
				},
			],
			[SAVE, WorkspaceService.saveWorkspace, DROP_ON_CLOSE],
			[SAVE_AS, (trace, { name, force }) => WorkspaceService.saveWorkspaceAs(trace, name, force), DROP_ON_CLOSE],
			[REMOVE, (trace, { name }) => WSM.removeWorkspace(trace, name), DROP_ON_CLOSE],
			[SWITCH_TO, WorkspaceService.handleSwitchTo],
			[
				SET_WORKSPACE_ORDER,
				(trace, data) =>
					WSM.setWorkspaceOrder(
						trace,
						data.map((x) => x.name)
					),
			],
			[GET_WORKSPACES, WSM.getWorkspaces],
			[SET_WINDOW_STATE, (trace, { windowName, state }) => WSM.setWindowState(trace, windowName, state), DROP_ON_CLOSE],
			[
				GET_WINDOW_STATE,
				(trace, { windowName, stateVars }: { windowName: string; stateVars: StateType[] }) =>
					WSM.getWindowState(trace, windowName, stateVars),
				DROP_ON_CLOSE,
			],
			[GET_WORKSPACE_NAMES, WSM.getWorkspaceNames],
			[ADD_WINDOW, WSM.addWindowToWorkspace, DROP_ON_CLOSE],
			[REMOVE_WINDOW, (trace, { name }) => WSM.removeWindowFromWorkspace(trace, name), DROP_ON_CLOSE],
			[RENAME, (trace, { oldName, newName }) => WSM.renameWorkspace(trace, newName, oldName)],
			[GET_ACTIVE_WORKSPACE_DIRTY, (trace) => WSM.isActiveWorkspaceDirty(trace)],
		];

		for (const [channel, handler, dropOnClose] of responders as [string, Function, boolean][]) {
			this.addResponder(channel, dropOnClose, handler);
		}

		// //////////////// Pub/Sub Listeners //////////////////
		let firstSubscribe = true;
		const dockingUpdate = async (err, { data: { groupData } }) => {
			if (firstSubscribe) {
				firstSubscribe = false;
				return;
			}

			const trace = setGlobalTrace(DOCKING.GROUP_UPDATE);
			if (WorkspaceService.lifecycle === "closing") {
				Logger.system.debug(
					`TRACE-${traceCounter}-Received publish from docking while closing the workspace. Dropping request.`
				);
				return;
			}
			/** DH 3/11/2019
			 * The behavior for topics prefixed with "Finsemble" is
			 * strange: when it first starts up, it publishes `undefined`;
			 * hence, this guard is necessary.
			 */
			if (groupData) {
				Logger.system.info(`TRACE-${traceCounter}-Received publish from docking, updating groups`);
				await WSM.setGroupData(trace, groupData);
				await WorkspaceService.publishUpdate(trace, DOCKING.GROUP_UPDATE);
			}
		};
		RouterClient.subscribe(DOCKING.GROUP_UPDATE, (err, data) => limit(() => dockingUpdate(err, data)));

		// /////////////// inner reset listener//////////////////
		RouterClient.addListener("Application.restart", (err, response) => {
			// when the user calls a "Reset" function, Finsemble shouldn't show a save workspace dialog.
			if (response.data?.forceRestart === true) {
				this.isResetRequested = true;
			} else if (!response.data?.forceRestart) {
				this.isRestartRequested = true;
			}

			// Prevents saving workspace on Restart
			if (response.data && response.data.scheduledCloseDiscardUnsavedChanges === true) {
				this.scheduledCloseDiscardUnsavedChanges = true;
			}
		});

		RouterClient.addListener("Application.shutdown", (err, response) => {
			// Prevents saving workspace on shutdown
			if (response.data && response.data.scheduledCloseDiscardUnsavedChanges === true) {
				this.scheduledCloseDiscardUnsavedChanges = true;
			}
		});
	}

	/**
	 * Publishes an update of the entire state of all workspaces.
	 * No-ops if the old state is the same as the new state.
	 *
	 * DH 3/11/2019 - This is a *lot* of data to be passing around
	 * to a very small audience. A reactive store like the Persistent
	 * store would obviate the need for an update like this: just
	 * query what you need, and receive updates as they happen.
	 */
	private static async publishUpdate(trace, reason) {
		try {
			const mappedReason = channelToOldReason[reason] || reason;
			const aws = await WSM.getActiveWorkspace(trace);
			const workspaces = await WSM.getWorkspaces(trace);
			// Converts workspaces array into a new array but leaving out the "windows" member (heavy stuff). This effectively returns just the workspaces meta/config information.
			const workspacesConfig = workspaces.map(({ ...config }) => config);

			//KW: it appears that the workspaceClient only uses the first two elements. We could consider dropping workspacesConfig
			const state: WorkspaceStateUpdate = {
				activeWorkspace: aws,
				workspaces: await WSM.getWorkspaceNames(trace),
				groups: aws.groups,
				reason: mappedReason,
				workspacesConfig,
			};

			if (isEqualIgnoringArrayOrder(state, WorkspaceService.cachedWorkspaceState)) {
				Logger.system.debug(LIFECYCLE, "No changes detected, skipping publish.", state, reason);
				return;
			}
			//disabled heavy log message containing all workspace state (twice)
			// Logger.system.debug(`TRACE-${trace.counter}-Publishing workspace update"`, state.activeWorkspace.name, {
			// 	...state,
			// 	cached: WorkspaceService.cachedWorkspaceState,
			// });
			WorkspaceService.cachedWorkspaceState = state;
			RouterClient.publish(UPDATE_PUBSUB, state);
		} catch (err) {
			Logger.system.debug("publishUpdate caught error", err);
		}
	}

	/**
	 * Returns true if should terminate current switch because a new switch request has come in
	 */
	static shouldStopSwitch(): boolean {
		return WorkspaceService.cleaningOutstandingSpawns || WorkspaceService.queuedSwitchRequest !== null;
	}

	/**
	 * Wait for p-queue's limitedSpawningQueue to become idle, indicating all pending spawns are
	 * completed. However, if the queue doesn't become idle within MAX_WAIT_TIME, then attempt to
	 * force-close the components and then force a completion.
	 *
	 * In theory the queue should always become idle quickly, but in practice slow-loading content or
	 * a system failure elsewhere can prevent the spawn completion.
	 */
	private static clearAndWaitForIdleSpawnQueue(): Promise<undefined> {
		//start by clearing the queue so that we don't generate any new pending spawns
		WorkspaceService.limitedSpawningQueue.clear();

		const MAX_WAIT_TIME = 1000;

		const promiseResolver = async (resolve) => {
			let forcedCompletion = false;
			Logger.system.log(
				`Waiting for idle spawn queue. Pending ${WorkspaceService.limitedSpawningQueue.pending} spawns...`
			);
			//set a timeout for pending spawns after which we'll attempt to force kill them
			const theTimer = setTimeout(() => {
				Logger.system.error(
					`${WorkspaceService.limitedSpawningQueue.pending} pending windows won't close gracefully. Forcing Workspace close completion. Pending window names:`,
					Object.keys(WorkspaceService.currentlySpawningWindowNames)
				);
				RouterClient.transmit("LauncherService.forceKillPendingWindows", {});
				forcedCompletion = true;
				resolve();
			}, MAX_WAIT_TIME);

			await WorkspaceService.limitedSpawningQueue.onIdle();
			if (!forcedCompletion) {
				clearTimeout(theTimer);
				resolve();
			}
		};
		return new Promise(promiseResolver);
	}

	static async saveWorkspace(trace: Trace) {
		try {
			await WorkspaceService.eventManager.trigger("save-requested");
			const success = await WorkspaceService.alertWindowLoadingDuringSave();
			await WSM.saveActiveWorkspace(trace);
			if (success) await WorkspaceService.eventManager.trigger("save-complete");
		} catch (err) {
			const error =
				"Workspace save failed. Could not write workspace data to storage. Internet connectivity may be down.";
			await WorkspaceService.eventManager.trigger("save-failed", { error });
			Logger.system.error("saveWorkspace failed error", err);
			toast("dev", "ALWAYS", "FSBL-Internal-Workspace-Error", error);
		}
	}

	static async saveWorkspaceAs(trace: Trace, name: string, force: boolean) {
		try {
			await WorkspaceService.eventManager.trigger("save-requested");
			const success = await WorkspaceService.alertWindowLoadingDuringSave();
			await WSM.saveActiveWorkspaceAs(trace, name, force);
			if (success) await WorkspaceService.eventManager.trigger("save-complete");
		} catch (err) {
			const error =
				"Workspace save failed. Could not write workspace data to storage. Internet connectivity may be down.";
			await WorkspaceService.eventManager.trigger("save-failed", { error });
			Logger.system.error("saveWorkspaceAs failed error", err);
			toast("dev", "ALWAYS", "FSBL-Internal-Workspace-Error", error);
		}
	}

	/**
	 * The main switch-to-new-workspace function that queues switch request if previous switch isn't complete.
	 */
	static async handleSwitchTo(trace: Trace, data: string | { name: string }) {
		Logger.system.log(
			"switch-trace handleSwitchTo",
			WorkspaceService.lifecycle,
			data,
			"queued flag",
			WorkspaceService.queuedSwitchRequest,
			WorkspaceService.cleaningOutstandingSpawns
		);

		const name = typeof data !== "string" ? (data as { name: string }).name : (data as string);

		// if a previous workspace load is still spawning components (even with state is "stable"), then clear its spawning queue
		if (
			WorkspaceService.limitedSpawningQueue &&
			(WorkspaceService.limitedSpawningQueue.size || WorkspaceService.limitedSpawningQueue.pending)
		) {
			WorkspaceService.cleaningOutstandingSpawns = true;
			Logger.system.log(
				`switch-trace CLEARING limitedSpawningQueue Queue with ${WorkspaceService.limitedSpawningQueue.pending} currently spawns pending`
			);
			// the above clear doesn't clear any spending promises, so have to wait  here for those pending to complete

			await WorkspaceService.clearAndWaitForIdleSpawnQueue();

			WorkspaceService.cleaningOutstandingSpawns = false;
		}

		// only switch workspace if in stable state
		if (WorkspaceService.lifecycle === "stable") {
			// handleSwitchTo may have been called multiple times and awaiting above, so immediately change state before any async operations to insure no concurrent switches, then do the switch
			WorkspaceService.lifecycle = "switching";
			try {
				await WorkspaceService.publishUpdate(trace, PUBLISH_REASONS.LOAD_STARTED);
				await WorkspaceService.coreSwitchTo(trace, data);
			} catch (err) {
				if (err == "Close was canceled") {
					Logger.system.log("handleSwitchTo: Workspace close was canceled.");
					await WorkspaceService.eventManager.trigger("close-canceled");
				} else {
					Logger.system.error(
						"handleSwitchTo: switch caught underlying error probably due to storage or internet failure",
						err
					);
					toast(
						"dev",
						"ALWAYS",
						"FSBL-Internal-Workspace-Error",
						"Workspace switch failed. Could not read workspace data from storage. Internet connectivity may be down."
					);
				}
			}

			WorkspaceService.lifecycle = "stable";
			Logger.system.log("switch-trace coreSwitchTo set to stable");

			// if a request was queued while doing coreSwitchTo, then can process it now
			if (WorkspaceService.queuedSwitchRequest) {
				Logger.system.log("switch-trace processing pending", WorkspaceService.lifecycle, data);

				const copyOfQueuedRequest = WorkspaceService.queuedSwitchRequest;
				// must clear the queued request before the recursive call to start all over
				WorkspaceService.queuedSwitchRequest = null;

				// recursive call to start all over with the queued request -- starting over ensures will always handle queued requests
				await WorkspaceService.handleSwitchTo(trace, copyOfQueuedRequest);
			}

			await WorkspaceService.publishUpdate(trace, PUBLISH_REASONS.LOAD_FINISHED);

			// otherwise queue the last request to switch workspaces --- if invoked multiple times only the last request is saved here
		} else {
			Logger.system.log("switch-trace queued", WorkspaceService.lifecycle, data);
			// queue only the last switch request (i.e. queue depth limited to 1), superseding any previously queued request
			WorkspaceService.queuedSwitchRequest = name;
		}
	}

	/**
	 * Private utility function to sleep/wait for a specified time before accepting the returned promise.
	 */
	private static sleep(milliseconds) {
		return new Promise((resolve) => setTimeout(resolve, milliseconds));
	}

	/**
	 * The lower-level switch-to-new-workspace function without the queuing: closes the active workspace and loads the workspace
	 * with the given name as the new active workspace.
	 *
	 * Returns the active workspace.
	 */
	static async coreSwitchTo(trace: Trace, data: string | { name: string }) {
		const STABILIZATION_TIME = 2000;
		let aws;
		let closeSucceeded;

		const name = typeof data !== "string" ? (data as { name: string }).name : (data as string);
		Logger.system.log(LIFECYCLE, `Switching to workspace "${name}"`);

		if (!(await WorkspaceService.isMasterStorageReachable(trace))) {
			Logger.system.error("switch-trace coreSwitchTo failed for getLastUsedWorkspaceName");
			throw new Error("Switch failed because master storage was not available");
		}

		try {
			Logger.system.log("switch-trace coreSwitchTo before close");
			WorkspaceService.lifecycle = "closing";
			closeSucceeded = await WorkspaceService.close(trace);
			Logger.system.log("switch-trace coreSwitchTo after close");
		} catch (err) {
			if (err === "event was canceled") {
				throw "Close was canceled";
			}
			Logger.system.error("switch-trace coreSwitchTo close failed", err);
			toast(
				"user",
				"ALWAYS",
				"FSBL-WorkspaceSwitch-Failure",
				WorkspaceService.closeWorkspaceTimeoutMessage.concat(
					" Could not read workspace data from storage. Internet connectivity may be down."
				)
			);
			closeSucceeded = false;
		}

		// If the workspace close failed, we create a new empty workspace and switch to it -- the idea
		const finalName = closeSucceeded ? name : await WSM.addWorkspace(trace, emptyWS);

		// if pending request to switch, then prematurely terminate the current switch by skipping the below
		if (!WorkspaceService.shouldStopSwitch()) {
			try {
				aws = await WSM.setActiveWorkspace(trace, finalName);
				await WorkspaceService.publishUpdate(trace, PUBLISH_REASONS.LOAD_DATA_RETRIEVED);

				// now load the new workspace, but don't await on load so that a new switchTo can happen even if there is a load problem (this eliminates the need for a load timeout)
				WorkspaceService.lifecycle = "loading";
				//pass aws into load here
				WorkspaceService.load(trace, finalName, aws)
					// this inner catch is required because the load is async, so outer catch won't work
					.catch((err) => {
						Logger.system.error("switch-trace coreSwitchTo load failed", err);
						toast(
							"dev",
							"ALWAYS",
							"FSBL-Internal-Workspace-Error",
							"Workspace load failed. Could not read workspace data from storage. Internet connectivity may be down."
						);
					});
			} catch (err) {
				Logger.system.error("switch-trace coreSwitchTo switch failed", err);
				toast(
					"dev",
					"ALWAYS",
					"FSBL-Internal-Workspace-Error",
					"Workspace load failed. Could not read workspace data from storage. Internet connectivity may be down."
				);
			}
		} else {
			Logger.system.log("switch-trace coreSwitchTo skipped since pending request", WorkspaceService.lifecycle, data);
		}

		// give time for any previous load to stabilize in case a queued switch is immediate initiated (and prematurely terminates still loading components)
		// Note: This sleep is required to side-step timing problems in the window service and/or FEA.
		await this.sleep(STABILIZATION_TIME);

		return {
			data: aws,
		};
	}

	/**
	 * Remove system-closed handlers for all windows.
	 * This is primarily for windows that were created via window.open.
	 * If the window that created them is closed, the child will close as well.
	 * The system-close fires, and then we remove the window from the workspace while the
	 * workspace is being switched.
	 *
	 * There's no valid reason to be listening for systemClose
	 * events when closing these windows as part of a workspace switch.
	 */
	private static async removeSystemClosedHandlers(windows: FinsembleWindowData[]): Promise<void> {
		for (let i = 0; i < windows.length; i++) {
			const windowInfo = windows[i];
			if (get(windowInfo, "isEvergreen", false)) {
				continue;
			}
			try {
				const { wrap } = await FinsembleWindow.getInstance({
					windowName: windowInfo.name,
					waitForReady: false,
				});
				wrap.removeEventListener("system-closed", (wrap as any)._systemClosed);
			} catch (e) {
				Logger.system.error(
					"switch-trace. Wrap failed. Trying to remove system-closed listener for window",
					windowInfo.name,
					e
				);
			}
		}
	}

	/**
	 * Closes all the windows belonging to the active workspace that have been spawned.
	 * Returns a boolean representing successful close.
	 *
	 * As noted above, this should eventually be replaced with a call
	 * to the coming Multi-Window Client.
	 */
	private static async close(trace: Trace): Promise<boolean> {
		Logger.system.log(LIFECYCLE, "Closing active workspace.");

		// note the retrieved list of all active-pending windows can include windows not in this workspace
		const { data: activeAndPendingWindows } = await deprecatedGetActiveAndPendingWindowNames(RouterClient);

		let workspaceWindows: FinsembleWindowData[] = [];
		const activeWorkspace = await WSM.getActiveWorkspace(trace);
		await WorkspaceService.eventManager.trigger("close-requested", activeWorkspace);
		const windowList = activeWorkspace.windows;
		try {
			workspaceWindows = await WSM.getManyStates("windowData", windowList);
		} catch (windowListErr) {
			Logger.system.error("Error retrieving list of windows to close.", windowListErr);
		}

		// make a list of the workspace windows that have been spawned (both spawn completed and pending) -- these are the windows to close;
		// note since a workspace load may have been preempted with another load, this list may only include a subset of the workspace windows
		const windows: FinsembleWindowData[] = workspaceWindows.filter((oneWindowObject) =>
			activeAndPendingWindows?.includes(oneWindowObject.name)
		);

		Logger.system.log(
			"switch-trace workspaceService.close windows",
			windows,
			"activeAndPendingWindows",
			activeAndPendingWindows,
			"workspaceWindows",
			workspaceWindows
		);

		// if there are no spawned windows to close, then done
		if (!windows.length) {
			await WorkspaceService.eventManager.trigger("close-complete");
			// else close all the spawned windows before proceeding
			Logger.system.log(LIFECYCLE, "Finished closing active workspace.");
			return true;
		}

		await WorkspaceService.removeSystemClosedHandlers(windows);

		let closeSucceeded = false;
		const TIMEOUT = WorkspaceService.closeWorkspaceTimeout;
		let windowsRemaining = [...windows];
		try {
			const closeFn = async (win: FinsembleWindowData) =>
				new Promise<void>(async (resolve) => {
					Logger.system.log(LIFECYCLE, `Closing window ${win.name}`);
					try {
						/** DH 3/5/2019
						 * getInstance doesn't resolve on all code paths.
						 * Wrapping it like this ensures the code continues.
						 */
						// if the spawn never completed then public wrap might not be fully available, so disabling waitForReady allows wrap to be created before window is fully ready.
						// this allows a workspace to be closed even though some of the spawns didn't complete (and the workspace load timed out)
						const { wrap } = await FinsembleWindow.getInstance({
							windowName: win.name,
							waitForReady: false,
						});

						Logger.system.log(`switch-trace closing ${win.name}`);

						const closeCallback = (error) => {
							if (error) Logger.system.error(error);
							windowsRemaining = windowsRemaining.filter((x) => x !== win);
							Logger.system.log(`switch-trace finished closing window ${win.name}`, error, windowsRemaining);
							Logger.system.log(LIFECYCLE, `finished closing window ${win.name}`);

							resolve();
						};

						if (win.isEvergreen) {
							Logger.system.verbose("Window is evergreen. Hiding", win.name, win);
							if (wrap.parentWindow) {
								await wrap.parentWindow.removeWindow({ windowIdentifier: wrap.identifier, noCloseStack: true });
							}
							wrap.hide({}, closeCallback);
						} else {
							Logger.system.verbose("Window is not evergreen. Closing", win.name, win);
							wrap.close(
								{
									removeFromWorkspace: false,
									ignoreParent: true,
									trace,
								},
								closeCallback
							);
						}
					} catch (error) {
						Logger.system.error("switch-trace close window", error);
						resolve();
					}
				});
			const p = new Promise<void>(async (resolve) => {
				const stacks = windows.filter(isStackedWindow);
				const notStacks = windows.filter(isNotStackedWindow);
				await Promise.all(notStacks.map(closeFn));
				await Promise.all(stacks.map(closeFn));

				// Resolve outer promise once all windows have closed
				resolve();
			});

			await wrapWithTimeout(
				p,
				TIMEOUT,
				`Attempt to close workspace windows timed out after ${TIMEOUT} ms. Please Reload Your Workspace. Remaining windows:`
			);
			closeSucceeded = true;
			await WorkspaceService.eventManager.trigger("close-complete");
		} catch (error) {
			Logger.system.error(error, windowsRemaining);
			await WorkspaceService.eventManager.trigger("close-failed", {
				error,
				data: windowsRemaining,
			});
			toast("user", "ALWAYS", "FSBL-WorkspaceSwitch-Failure", WorkspaceService.closeWorkspaceTimeoutMessage);
		}

		Logger.system.log(LIFECYCLE, "Finished closing active workspace.");

		return closeSucceeded;
	}

	private static async spawn({
		name,
		win,
		groupData,
		windowsWhichAreChildrenOfAStack,
		windowsInError,
	}: {
		name: string;
		win: any;
		groupData: Record<string, GroupData>;
		windowsWhichAreChildrenOfAStack: any[];
		windowsInError: any[];
	}) {
		/** DH 3/5/2019
		 * Component state is stored in several places right now.
		 */
		const component = get(win, "customData.component.type", win.componentType);
		Logger.system.debug(LIFECYCLE, "Launching component", win.name);

		// If the current window is a stack child, we want to hide it when it spawns until the stack is ready/calls it to be visible
		if (windowsWhichAreChildrenOfAStack.includes(win.name)) {
			win.autoShow = false;
		}

		const componentSpawnParams: {
			spawnedByWorkspaceService: boolean;
			addToWorkspace: boolean;
			options: any;
			forceOntoMonitor?: boolean | string;
			isInExistingGroup?: boolean;
			width?: any;
			height?: any;
			top?: any;
			left?: any;
		} = {
			spawnedByWorkspaceService: true,
			addToWorkspace: false,
			options: win,
			height: win.height,
			width: win.width,
			top: win.top,
			left: win.left,
		};
		// If window is in a group don't force it onto monitor on spawn
		// Groups will be handled later to make sure they stay intact
		if (groupData) {
			for (const groupName in groupData) {
				const groupWindows = groupData[groupName].windowNames;
				if (groupWindows.includes(win.name) && groupData[groupName].isMovable) {
					componentSpawnParams.forceOntoMonitor = false;
					componentSpawnParams.isInExistingGroup = true;
					break;
				}
			}
		}
		try {
			// premature termination is handled elsewhere, but this is an extra defensive check
			if (WorkspaceService.shouldStopSwitch()) {
				Logger.system.log(`switch-trace spawnPromise cleared for ${win.name} since pending request`);
				return;
			}

			Logger.system.log(`switch-trace spawnPromise for ${win.name}`);

			//keep a record of window names that are mid spawn in case we need to kill them off during a switch
			WorkspaceService.currentlySpawningWindowNames[win.name] = win.name;
			await LauncherClient.spawn(component, componentSpawnParams);
			delete WorkspaceService.currentlySpawningWindowNames[win.name];

			if (win.isEvergreen) {
				// This should ideally be done in the window service but currently difficult to get the
				// workspace data in the window service

				// Update the linker
				const componentState = (await WSM.getManyStates("componentState", [win.name], name)) as any[];
				RouterClient.transmit("LinkerService-Event-component-state-changed", {
					windowName: win.name,
					Finsemble_Linker:
						componentState.length > 0 && componentState[0].Finsemble_Linker ? componentState[0].Finsemble_Linker : null,
				});

				// Notify component it's internal state has changed.
				const { wrap } = await FinsembleWindow.getInstance({
					windowName: win.name,
					waitForReady: true,
				});
				await RouterClient.query("DockingService.registerWindow", { name: win.name }, () => {});
				await RouterClient.query(
					"WindowService-Request-componentStateChanged",
					{ windowIdentifier: wrap.identifier, componentState },
					() => {}
				);
			}
		} catch (error) {
			Logger.system.error(`switch-trace spawnPromise error for ${win.name}`, error);
			windowsInError.push(win.name);
		}
	}

	private static async spawnWorkspace(name: string, aws: Workspace, windowData: FinsembleWindowData[]) {
		// ******************************************************************************************************************************
		// TABBING: here need to add code to spawn in two groups: 1) first all the not stacked windows 2) then the stackedWindows
		//
		// Ideally the workspace manager would only spawn the stacked window, letting the stacked window spawn its children -- this
		// approach would provide for a orderly startup, but it also impacts too much code on first pass. The simple approach used
		// does introduces some startup timing holes, but hopefully won't matter because tabbing related activity should
		// be minimal.  Still, some tweaks for startup might be required.
		// ******************************************************************************************************************************
		const stacks = windowData.filter(isStackedWindow);
		const notStacks = windowData.filter(isNotStackedWindow);
		// Create a list of all window names which are children of a stack
		const windowsWhichAreChildrenOfAStack: any[] = [];
		const stackChildNames = stacks.map((stack) => stack.childWindowIdentifiers?.map((child) => child.windowName));
		notStacks.forEach((instance) => {
			const { name: winName } = instance;
			stackChildNames.forEach((names) => {
				if (names?.includes(winName) === true) {
					windowsWhichAreChildrenOfAStack.push(winName);
				}
			});
		});
		const windowsInError: any[] = [];

		Logger.system.log("switch-trace load limitedSpawningQueue allocated", name);

		// limit the number of concurrent spawns (in part to side-step "third-party" spawn bug, but also to distribute load over time);
		// save the limit function in class variable so can use in workspace close() to prematurely eliminate outstanding spawns if closed occurs before the load is complete.
		WorkspaceService.limitedSpawningQueue = new PQueue({
			concurrency: WorkspaceService.concurrentSpawnLimit,
		});
		WorkspaceService.currentlySpawningWindowNames = {};

		try {
			const loadTimer = setTimeout(() => {
				clearTimeout(loadTimer);
				Logger.system.warn(
					"WARNING: Workspace is taking a long time to load. Some components may have returned an error when spawning",
					windowsInError
				);
				WorkspaceService.eventManager.trigger("load-failed", {
					error: "Workspace Load Failed. Some Workspace Components are taking too long to load",
					data: windowsInError,
				});
			}, 60000);

			// spawn all the non-stacked windows first and wait for them to complete (any stacked windows will assume the children are already spawned)
			Logger.system.debug(LIFECYCLE, "Launching component spawnNotStacks", notStacks);
			const spawnNotStacks = notStacks.map((win) =>
				WorkspaceService.limitedSpawningQueue.add(() =>
					WorkspaceService.spawn({
						name,
						win,
						groupData: aws.groups,
						windowsWhichAreChildrenOfAStack,
						windowsInError,
					})
				)
			);
			await Promise.all(spawnNotStacks);

			// while the previous Promise.all was executing, the state may have changed, so only continue with next Promise.all if not prematurely terminating the load
			if (!WorkspaceService.shouldStopSwitch()) {
				// now can spawn all the stacked windows (and wait for them to complete)
				Logger.system.debug(LIFECYCLE, "Launching component spawnStacks", stacks);
				const spawnStacks = stacks.map((win) =>
					WorkspaceService.limitedSpawningQueue.add(() =>
						WorkspaceService.spawn({
							name,
							win,
							groupData: aws.groups,
							windowsWhichAreChildrenOfAStack,
							windowsInError,
						})
					)
				);
				await Promise.all(spawnStacks);
				clearTimeout(loadTimer);
				await WorkspaceService.eventManager.trigger("load-complete");
			} else {
				clearTimeout(loadTimer);
				await WorkspaceService.eventManager.trigger("load-interrupted");
				Logger.system.debug(LIFECYCLE, "bypassing the launching of spawnStacks since prematurely terminated", stacks);
			}
		} catch (error) {
			Logger.system.error(`Workspace ${name} load: ${error}`);
		}
	}

	/**
	 * Launches all windows in the active workspace.
	 */
	private static async load(trace: Trace, name: string, aws: Workspace): Promise<boolean | undefined> {
		Logger.system.log(LIFECYCLE, `Loading workspace "${name}"`);
		Logger.system.log("switch-trace load", name);
		let loadSucceeded = true;

		//retrieve windowData for use in load-requested event and spawning of workspace
		let windowData = await WSM.getManyStates("windowData", aws.windows);
		//clone the active workspace for use in load-requested event
		let eventTarget = clone<any>(aws);
		// Component State cannot be modified so is not sent over in event
		// also not used in rest of this function
		delete eventTarget.componentState;

		// Add window data into eventTarget. It could be modified at the `load-requested` listener
		eventTarget.windowData = windowData;
		const updatedData = await WorkspaceService.eventManager.trigger("load-requested", eventTarget);
		if (updatedData) {
			//apply updates from event handler here
			//the parameter for the `merge` function to prevent duplicating values in arrays
			const overwriteMerge = (destinationArray, sourceArray) => sourceArray;
			for (const update of updatedData) {
				if (update) {
					if (update.windowData) {
						// update window data separatly from the Workspace data
						windowData = merge(windowData, update.windowData, { arrayMerge: overwriteMerge });

						// remove windowData before update workspace
						delete update.windowData;
					}
					aws = merge(aws, update, { arrayMerge: overwriteMerge });
				}
			}
			//perhaps workspacemanager should be notified of updates too...
			//would need to expose updateActiveWorkspace to bypass isdirty logic
			await WSM.updateActiveWorkspace(trace, () => aws);
			// Persist any changes specified into storage for activeWorkspace.
			// Note that changes will dirty the workspace as it will diverge from the stored workspace.
			await WSM.updateWindowDataForActiveWorkspace(windowData, aws);
		}

		// Tell the launcher service to reset the stagger algorithm
		RouterClient.transmit("Launcher.resetSpawnStagger", {});

		// If the workspace is empty, we're done.
		if (!eventTarget.windows.length) {
			try {
				await WorkspaceService.publishUpdate(trace, PUBLISH_REASONS.LOAD_FINISHED);
				await WorkspaceService.eventManager.trigger("load-complete");
			} catch (publishUpdateErr) {
				Logger.system.error(
					"Windows have been closed and the new workspace has been loaded but a problem was encountered updating the service with new status",
					publishUpdateErr
				);
			}
			return;
		}

		await WorkspaceService.spawnWorkspace(name, aws, windowData);

		// get the real name to save from WSM because here the name might be "active-workspace" if loading from cache
		WSM.saveLastUsedWorkspaceName(trace, WSM.cachedActiveWorkspaceName);

		await WorkspaceService.publishUpdate(trace, PUBLISH_REASONS.LOAD_FINISHED);
		Logger.system.log(LIFECYCLE, "Finished loading workspace");

		return loadSucceeded;
	}

	/**
	 * Brings all windows in the active workspace to front.
	 * (Just a pass-through to the LauncherClient).
	 */
	static async handleBringWindowsToFront(trace: Trace) {
		const windowList = (await WSM.getActiveWorkspace(trace)).windows;
		return new Promise((resolve) => {
			bringWindowsToFront({ windowList }, resolve);
		});
	}

	/**
	 * Minimizes all windows in the active workspace.
	 * (Just a pass-through to the LauncherClient).
	 */
	static async handleMinimizeAll(trace: Trace) {
		const windowList = (await WSM.getActiveWorkspace(trace)).windows;
		return new Promise((resolve) => {
			minimizeWindows({ windowList }, resolve);
		});
	}

	/**
	 * Notifies the user if any windows should be added to the workspace, but are not because they are still
	 * in the process of loading.
	 *
	 * @static
	 * @memberof WorkspaceService
	 */
	static async alertWindowLoadingDuringSave() {
		const { err, response } = await RouterClient.query("Launcher.getPendingWindows", {});
		if (err) {
			Logger.system.error(
				"Unable to establish that all windows had finished loading udring save operation. Some windows may not ahve been saved.",
				err
			);
		}
		if (response && response.data) {
			let pendingWindows = response.data;
			const winsToLog = Object.keys(pendingWindows)
				.filter((name) => pendingWindows[name].addToWorkspace)
				.join("\n ");
			if (winsToLog) {
				Logger.system.log(
					`The workspace was saved before all windows finished loading. The following windows were not saved in the workspace: \n ${winsToLog}`
				);
				const error =
					"Some windows were not saved. To avoid losing data, please wait for all components to load completely before saving a workspace.";
				toast("user", "ALWAYS", "FSBL-WorkspaceSave-LoadWarning", error);
				await WorkspaceService.eventManager.trigger("save-failed", {
					error: `Workspace Save Failed. ${error}`,
					data: winsToLog,
				});
				return false;
			}
			return true;
		}
	}
}

const serviceInstance = new WorkspaceService({
	name: "workspaceService",
	startupDependencies: {},
});

serviceInstance.onBaseServiceReady(async (cb) => {
	await SystemManagerClient.waitForStartup("assimilationService");
	await SystemManagerClient.waitForStartup("windowService");
	serviceInstance.initialize().then(cb);
	serviceInstance.setReady();
});

serviceInstance.start();
